/// <amd-module name="Test/TestPlansHubRefresh/TestHub/Redux/Suites/SuitesSaga" />

import * as React from "react";
import * as Resources from "Test/TestPlansHubRefresh/TestHub/Resources";
import * as Utils_String from "VSS/Core/Util/String";

import { Channel, SagaIterator, channel, effects } from "redux-saga";
import {
    CloneTestSuiteOperationInformation,
    SuiteEntryTypes,
    SuiteEntryUpdateParams,
    SuiteExpand,
    TestEntityCount,
    TestEntityTypes,
    TestPlan,
    TestSuite,
    TestSuiteCreateParams,
    TestSuiteType,
    TestSuiteUpdateParams
} from "TestPlanCommon/Contracts/TestPlan";
import { IFilterItemState, IFilterState } from "VSSUI/Utilities/Filter";
import { ILegacyComponentProperties, LegacyComponent } from "VSS/Legacy/Legacy";
import { ISettingsService, SettingsUserScope } from "VSS/Platform/Settings";
import { IVssLayoutManager, WrappedComponent } from "VSS/Platform/Layout";
import { RouteParametersMineAllPage, SuitesConstants, TelemetryConstants, TestHubRefreshConstants } from "Test/TestPlansHubRefresh/TestHub/Constants";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { SuitesActions, SuitesActionsTypes } from "./SuitesActions";
import { errorHandledSaga, getDataProviderContent, getSelectionSuiteIndexFromSuiteId } from "Test/TestPlansHubRefresh/TestHub/Util";
import {
    getDefineColumnOptionFields,
    getExecuteColumnOptionFields,
    getSelectedSuiteId,
    isAdvancedLicenseEnabled,
    isShowTestsFromChildSuitesEnabled
} from "Test/TestPlansHubRefresh/TestHub/Redux/TestHubRefresh/TestHubRefreshSelector";
import {
    getIdToTestSuiteMap,
    getIdToTreeItemExpandableStateMap,
    getTestSuiteById,
    getTestSuites
} from "Test/TestPlansHubRefresh/TestHub/Redux/Suites/SuitesSelector";

import { ActionsOfType } from "redux/Utils/ActionHelper";
import { CloneOperationState } from "Test/TestPlansHubRefresh/TestHub/Redux/TestHubRefresh/TestHubRefreshContracts";
import { IGlobalMessagesService } from "TFS/GlobalBanner/GlobalMessagesService";
import { ITestHubDataService } from "Test/TestPlansHubRefresh/Service/TestHubDataService";
import { ITfsPageService } from "Tfs/Platform/Page";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssLocationService } from "VSS/Platform/Location";
import { IVssPageContext } from "VSS/Platform/Context";
import { IWorkItemService } from "Wit/WorkItem/Service/Contracts";
import { TelemetryActions } from "Test/TestPlansHubRefresh/TestHub/Redux/TelemetryActions";
import { TestHubRefreshActions, TestHubRefreshActionsTypes } from "Test/TestPlansHubRefresh/TestHub/Redux/TestHubRefresh/TestHubRefreshActions";
import { format } from "VSS/Core/Util/String";
import { getAllColumnFields as getDefineTabColumns } from "Test/TestPlansHubRefresh/Pivots/ColumnOptions/DefineColumnOptionUtils";
import { getAllColumnFields as getExecuteTabColumns } from "Test/TestPlansHubRefresh/Pivots/ColumnOptions/ExecuteColumnOptionUtils";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { testHubRefreshLocalContextData } from "Test/TestPlansHubRefresh/TestHub/Utils/TestPlansHubRefreshLocalContextProvider";

let showLargeDataSetToast: boolean = true;

interface ICreateEditSuiteQueryPayload {
    testSuite: TestSuite;
    planId: number;
    parentSuiteId: number;
    parentSuiteTitle: string;
    suiteType: TestSuiteType;
    rootSuiteId: number;
    queryName: string;
    queryText: string;
}

export function* SuitesSaga(): SagaIterator {
    yield effects.takeLatest(SuitesActionsTypes.reloadTestPlanAndSuites, _reloadTestPlanAndSuites);
    yield effects.takeLatest(TestHubRefreshActionsTypes.saveTestCasePreviewStatus, _saveTestCasePreviewStatus);
    yield effects.takeLatest(TestHubRefreshActionsTypes.fetchTestCasePreviewStatus, _fetchTestCasePreviewStatus);
    yield effects.takeLatest(SuitesActionsTypes.getSuiteCloneOpState, _getSuiteCloneOpState);
    yield effects.takeLatest(SuitesActionsTypes.loadInitialTestData, _loadTestData);
    yield effects.takeLatest(SuitesActionsTypes.getMineAllPageUrl, _getMineAllPageUrl);
    yield effects.takeLatest(SuitesActionsTypes.getSuitePointCount, _getSuitePointCount);
    yield effects.takeLatest(SuitesActionsTypes.getSuiteTestCasesCount, _getSuiteTestCasesCount);
    yield effects.takeLatest(SuitesActionsTypes.openTestSuiteWitForm, _openTestSuiteWitForm);
    yield effects.takeLatest(SuitesActionsTypes.createNewReqSuite, _createNewReqSuite);
    yield effects.takeLatest(SuitesActionsTypes.openDeleteTestSuiteDialog, _openDeleteTestSuiteDialog);
    yield effects.takeLatest(SuitesActionsTypes.deleteTestSuite, _deleteTestSuiteAction);
    yield effects.takeLatest(SuitesActionsTypes.createStaticSuite, _createStaticSuite);
    yield effects.takeLatest(SuitesActionsTypes.saveTestSuiteName, _saveTestSuiteName);
    yield effects.takeLatest(SuitesActionsTypes.openCreateEditSuiteQueryForm, _openCreateEditSuiteQueryForm);
    yield effects.takeLatest(SuitesActionsTypes.createNewQuerySuite, _createNewQuerySuite);
    yield effects.takeLatest(SuitesActionsTypes.editQueryForQueryBasedSuite, _editQueryForQueryBasedSuite);
    yield effects.takeLatest(SuitesActionsTypes.updateTestSuiteParent, _updateTestSuiteParent);
    yield effects.takeLatest(SuitesActionsTypes.reorderTestSuite, _reorderTestSuite);
    yield effects.takeLatest(SuitesActionsTypes.updateParentAndReorderSuite, _updateParentAndReorderSuite);
    yield effects.takeLatest(SuitesActionsTypes.moveTestCaseToSuite, _moveTestCaseToSuite);
    yield effects.takeLatest(SuitesActionsTypes.assignTestersToSuite, _assignTestersToSuite);
    yield effects.takeLatest(SuitesActionsTypes.openExportHTMLDialog, _openExportHTMLDialog);
    yield effects.takeLatest(SuitesActionsTypes.saveSplitterWidth, _saveSplitterWidth);
    yield effects.takeLatest(SuitesActionsTypes.fetchSplitterWidth, _fetchSplitterWidth);
    yield effects.takeLatest(SuitesActionsTypes.showToastNotifications, showToastNotifications);
    yield effects.takeLatest(SuitesActionsTypes.openRequirementWorkItemForm, _openRequirementWorkItemForm);
}

export function* reloadTestPlanAndSuites(action: ActionsOfType<SuitesActions, SuitesActionsTypes.reloadTestPlanAndSuites>): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const dataService: ITestHubDataService = (yield effects.call([pageContext, pageContext.getService], "ITestHubDataService")) as any;

    const testPlan = (yield effects.call([dataService, dataService.getTestPlanById], action.payload.planId)) as any;
    yield effects.put(SuitesActions.loadTestPlan(testPlan));

    const testSuites = (yield effects.call(
        [dataService, dataService.getTestSuitesForPlan],
        action.payload.planId,
        SuiteExpand.Children,
        "",
        true
    )) as any;
    yield effects.put(SuitesActions.loadTestSuites(testSuites));

    let selectedSuiteId = testPlan.rootSuite.id;
    let selectedSuiteName = testPlan.rootSuite.name;
    if (testSuites[0].hasChildren) {
        selectedSuiteId = testSuites[0].children[0].id; // first child of rootsuite is the selected suite
        selectedSuiteName = testSuites[0].children[0].name;
    }

    yield effects.put(SuitesActions.updateReloadState(true));
    yield effects.put(SuitesActions.changeSelectedSuite(testPlan.id, selectedSuiteId, selectedSuiteName));
    yield effects.put(SuitesActions.fetchSplitterWidth());

    const contributionService: IVssContributionService = (yield effects.call(
        [pageContext, pageContext.getService],
        "IVssContributionService"
    )) as any;
    yield effects.call([contributionService, contributionService.getContributionsAsync], ["ms.vss-test-web.breadcrumb-service"]);
    const breadcrumbService = (yield effects.call([pageContext, pageContext.getService], "testplans-hub-refresh-breadcrumb-service")) as any;
    if (breadcrumbService) {
        breadcrumbService.clearTestPlanNameBreadcrumb();
        breadcrumbService.addBreadCrumb("Hub", testPlan.name, undefined, TestHubRefreshConstants.BREADCRUMB_PRIRORITY);
    }

    if (testHubRefreshLocalContextData.getCurrentPivot() === TestHubRefreshConstants.DEFINE_TAB_ID) {
        yield effects.put(SuitesActions.getSuiteTestCasesCount(action.payload.planId, [], []));
    } else if (testHubRefreshLocalContextData.getCurrentPivot() === TestHubRefreshConstants.EXECUTE_TAB_ID) {
        yield effects.put(SuitesActions.getSuitePointCount(action.payload.planId, [], [], [], []));
    }
}

export function* getSuiteCloneOpState(action: ActionsOfType<SuitesActions, SuitesActionsTypes.getSuiteCloneOpState>): SagaIterator {
    try {
        const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
        const dataService: ITestHubDataService = (yield effects.call([pageContext, pageContext.getService], "ITestHubDataService")) as any;
        // We need to show a toast message on the initial call.
        if (action.payload.initialCall) {
            yield effects.put(SuitesActions.showToastNotifications(Resources.SuiteImportInitiated, TestHubRefreshConstants.TOAST_MESSAGE_DURATION));
        }

        let cloneTestSuiteOperationInformation: CloneTestSuiteOperationInformation = (yield effects.call(
            [dataService, dataService.getSuiteCloneInformation],
            action.payload.opId,
            action.payload.project
        )) as any;
        let state: CloneOperationState = CloneOperationState.Queued;
        if (cloneTestSuiteOperationInformation && cloneTestSuiteOperationInformation.cloneOperationResponse) {
            state = cloneTestSuiteOperationInformation.cloneOperationResponse.state;
        }

        if (state !== CloneOperationState.Succeeded) {
            yield effects.delay(5000);
            yield effects.put(SuitesActions.getSuiteCloneOpState(action.payload.planId, action.payload.opId, false, action.payload.project));
        } else {
            const refreshAfterSuiteImportChannel: Channel<{}> = (yield effects.call(channel)) as any;

            // Show a toast message indicating that the clone operation has completed.
            yield effects.put(
                SuitesActions.showToastNotifications(Resources.SuiteImportCompleted, 5000, Resources.Refresh, () => {
                    refreshAfterSuiteImport(refreshAfterSuiteImportChannel, action.payload.planId);
                })
            );

            while (true) {
                const payload = (yield effects.take(refreshAfterSuiteImportChannel)) as any;
                yield effects.put(SuitesActions.reloadTestPlanAndSuites(payload.planId));
            }
        }
    } catch (e) {
        yield effects.put(SuitesActions.showToastNotifications(e.message, 5000));
    }
}

function refreshAfterSuiteImport(refreshAfterSuiteImportChannel: Channel<any>, planId: number) {
    refreshAfterSuiteImportChannel.put({ planId: planId });
}

export function* loadTestData(action: ActionsOfType<SuitesActions, SuitesActionsTypes.loadInitialTestData>): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const settingsService: ISettingsService = (yield effects.call([pageContext, pageContext.getService], "ISettingsService")) as any;
    const tfsPageService: ITfsPageService = (yield effects.call([pageContext, pageContext.getService], "ITfsPageService")) as any;
    const tfsPageData = (yield effects.call([tfsPageService, tfsPageService.getData])) as any;

    const data = getDataProviderContent(pageContext);
    yield effects.put(SuitesActions.loadInitialTestDataSucceeded(data));

    const startTime = performance.now();
    const suitesData: ILoadSuitesTelemetryPayload = (yield effects.call(getSuitesCountAndHierarchy, data.testSuites)) as any;
    const endTime = performance.now();

    // store the selected plan id in the registry entries
    if (data.testPlan && data.testPlan.id > 0) {
        let registryEntries;
        registryEntries = {
            SelectedPlanId: data.testPlan.id
        };
        yield effects.call([settingsService, settingsService.setEntries], registryEntries, SettingsUserScope.Me, "project", tfsPageData.project.id);
    }
    // store the selected suite id in the registry entries
    if (data.selectedSuiteId > 0) {
        let registryEntries;
        registryEntries = {
            SelectedSuiteId: data.selectedSuiteId
        };
        yield effects.call([settingsService, settingsService.setEntries], registryEntries, SettingsUserScope.Me, "project", tfsPageData.project.id);
    }
    const dataService: ITestHubDataService = (yield effects.call([pageContext, pageContext.getService], "ITestHubDataService")) as any;

    const testPlan = (yield effects.call([dataService, dataService.getTestPlanById], data.testPlan.id)) as any;
    let selectedBuild = testPlan.buildId ? testPlan.buildId : "0";
    let selectedBuildDefnId = testPlan.buildDefinition && testPlan.buildDefinition.id ? testPlan.buildDefinition.id : 0;
    yield effects.put(TestHubRefreshActions.updateSelectedBuild(selectedBuild, selectedBuildDefnId));
    let selectedReleaseId =
        testPlan.releaseEnvironmentDefinition && testPlan.releaseEnvironmentDefinition.definitionId
            ? testPlan.releaseEnvironmentDefinition.definitionId
            : 0;
    let selectedReleaseEnvironmentId =
        testPlan.releaseEnvironmentDefinition && testPlan.releaseEnvironmentDefinition.environmentDefinitionId
            ? testPlan.releaseEnvironmentDefinition.environmentDefinitionId
            : 0;
    yield effects.put(TestHubRefreshActions.updateSelectedRelease(selectedReleaseId, selectedReleaseEnvironmentId));

    const telemetryPayload = {
        suitesCount: { RBS: suitesData.rbs, QBS: suitesData.qbs, Static: suitesData.static },
        suitesHierarchy: suitesData.hierarchy,
        planId: data.testPlan.id,
        selectedSuiteId: data.selectedSuiteId,
        timeElapsed: endTime - startTime,
        isAdvancedLicenseEnabled: yield effects.select(isAdvancedLicenseEnabled)
    };
    yield effects.put(TelemetryActions.publishTelemetry(TelemetryConstants.GET_TEST_SUITES_FOR_PLAN, telemetryPayload));
    yield effects.put(SuitesActions.fetchSplitterWidth());
    yield effects.put(TestHubRefreshActions.fetchTestCasePreviewStatus());
}

export function* getSuitePointCount(action: ActionsOfType<SuitesActions, SuitesActionsTypes.getSuitePointCount>): SagaIterator {
    // get point count only if the current pivot is execute
    if (testHubRefreshLocalContextData.getCurrentPivot() === TestHubRefreshConstants.EXECUTE_TAB_ID) {
        const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
        const dataService: ITestHubDataService = (yield effects.call([pageContext, pageContext.getService], "ITestHubDataService")) as any;
        let pointCount: TestEntityCount[];

        if (isFeatureFlagEnabled(pageContext, TestHubRefreshConstants.DisableEntityCountFF, false)) {
            pointCount = [];
        } else {
            try {
                pointCount = (yield effects.call(
                    [dataService, dataService.fetchTestEntityCount],
                    action.payload.planId,
                    action.payload.states,
                    action.payload.configurations,
                    action.payload.testers,
                    action.payload.assignedTo,
                    action.payload.outcome,
                    TestEntityTypes.TestPoint
                )) as any;
            } catch (error) {
                pointCount = [];
                console.error(error);
            }
        }
        const showTestsFromChildSuitesEnabled = (yield effects.select(isShowTestsFromChildSuitesEnabled)) as any;
        yield effects.put(SuitesActions.showSuitePointsCount(action.payload.planId, pointCount, showTestsFromChildSuitesEnabled));
        const isAdvLicense: boolean = (yield effects.select(isAdvancedLicenseEnabled)) as any;
        if (isAdvLicense && isFeatureFlagEnabled(pageContext, TestHubRefreshConstants.ShowLargeDatasetToastFF, false) && showLargeDataSetToast) {
            let largeDataSetSuites: number[] = [];
            for (let i = 0; i < pointCount.length; i++) {
                if (pointCount[i].totalCount > TestHubRefreshConstants.LARGE_DATASET_COUNT_THRESHOLD) {
                    largeDataSetSuites.push(pointCount[i].testSuiteId);
                }
            }
            if (largeDataSetSuites.length > 0) {
                showLargeDataSetToast = false;
                if (largeDataSetSuites.length === 1) {
                    yield effects.put(
                        SuitesActions.showToastNotifications(
                            format(Resources.LargeDataSetToast_singleSuite, largeDataSetSuites[0]),
                            TestHubRefreshConstants.LONG_TOAST_MESSAGE_DURATION
                        )
                    );
                } else {
                    yield effects.put(
                        SuitesActions.showToastNotifications(
                            Resources.LargeDataSetToast_multipleSuites,
                            TestHubRefreshConstants.LONG_TOAST_MESSAGE_DURATION
                        )
                    );
                }
            }
        }
    }
}

export function* getSuiteTestCasesCount(action: ActionsOfType<SuitesActions, SuitesActionsTypes.getSuiteTestCasesCount>): SagaIterator {
    // get test case count when current pivot is define
    if (testHubRefreshLocalContextData.getCurrentPivot() === TestHubRefreshConstants.DEFINE_TAB_ID) {
        const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
        const dataService: ITestHubDataService = (yield effects.call([pageContext, pageContext.getService], "ITestHubDataService")) as any;
        let testCaseCount: TestEntityCount[];
        if (isFeatureFlagEnabled(pageContext, TestHubRefreshConstants.DisableEntityCountFF, false)) {
            testCaseCount = [];
        } else {
            try {
                testCaseCount = (yield effects.call(
                    [dataService, dataService.fetchTestEntityCount],
                    action.payload.planId,
                    action.payload.states,
                    null,
                    null,
                    action.payload.assignedTo,
                    null,
                    TestEntityTypes.TestCase
                )) as any;
            } catch (error) {
                testCaseCount = [];
                console.error(error);
            }
        }
        yield effects.put(SuitesActions.showSuiteTestCasesCount(action.payload.planId, testCaseCount));
    }
}

export function* openTestSuiteWitForm(action: ActionsOfType<SuitesActions, SuitesActionsTypes.openTestSuiteWitForm>): SagaIterator {
    const UpdateTestSuiteChannel: Channel<IUpdateTestSuitePayload> = (yield effects.call(channel)) as any;
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const contributionService: IVssContributionService = (yield effects.call(
        [pageContext, pageContext.getService],
        "IVssContributionService"
    )) as any;
    yield effects.call(
        [contributionService, contributionService.getContributionsAsync],
        ["ms.vss-features.ui-responsive-viewport-content", "ms.vss-work-web.common-content"]
    );
    const workItemService: IWorkItemService = (yield effects.call([pageContext, pageContext.getService], "work-item-service")) as any;
    if (workItemService) {
        yield effects.call([workItemService, workItemService.openWorkItem], action.payload.testSuiteId, {
            save: (workItem: any): void => {
                const newTestSuiteTitle = workItem.getTitle();
                UpdateTestSuiteChannel.put({
                    suiteId: action.payload.testSuiteId,
                    suiteTitle: newTestSuiteTitle,
                    rootSuiteId: action.payload.rootSuiteId
                });
            }
        });
    } else {
        throw new Error("Unable to fetch workitem service instance.");
    }

    const openTestSuiteTelemetryPayload = {
        suiteId: action.payload.testSuiteId,
        planId: action.payload.testPlanId,
        isAdvancedLicenseEnabled: yield effects.select(isAdvancedLicenseEnabled)
    };
    yield effects.put(TelemetryActions.publishTelemetry(TelemetryConstants.OEPN_TEST_SUITE_USING_FORM, openTestSuiteTelemetryPayload));
    const payload: IUpdateTestSuitePayload = (yield effects.take(UpdateTestSuiteChannel)) as any;
    if (Utils_String.localeComparer(payload.suiteTitle, action.payload.testSuiteTitle)) {
        yield effects.put(SuitesActions.saveTestSuiteName(action.payload.testPlanId, payload.suiteId, action.payload.testSuiteTitle, payload.suiteTitle, payload.rootSuiteId));
    }

    const editTestSuiteTelemetryPayload = {
        suiteId: payload.suiteId,
        planId: action.payload.testPlanId,
        isAdvancedLicenseEnabled: yield effects.select(isAdvancedLicenseEnabled)
    };
    yield effects.put(TelemetryActions.publishTelemetry(TelemetryConstants.EDIT_TEST_SUITE_USING_FORM, editTestSuiteTelemetryPayload));
}

export function* createNewReqSuite(action: ActionsOfType<SuitesActions, SuitesActionsTypes.createNewReqSuite>): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const dataService: ITestHubDataService = (yield effects.call([pageContext, pageContext.getService], "ITestHubDataService")) as any;
    let testSuiteList: TestSuite[] = [];
    let testSuites: TestSuite[] = [];
    yield effects.put(TestHubRefreshActions.updateBusyViewStatus(true));
    const testSuiteMap: Map<number, TestSuite> = (yield effects.select(getIdToTestSuiteMap)) as any;
    const params: TestSuiteCreateParams[] = [];
    if (isFeatureFlagEnabled(pageContext, TestHubRefreshConstants.BulkCreateRequirementBasedSuitesFF, false)) {
        for (let i = 0; i < action.payload.requirementIds.length; i++) {
            const param = {
                suiteType: action.payload.suiteType,
                requirementId: action.payload.requirementIds[i]
            } as TestSuiteCreateParams;
            params.push(param);
        }
        if (params.length > 0) {
            testSuites = (yield effects.call(
                [dataService, dataService.createBulkTestSuites],
                params,
                action.payload.planId,
                action.payload.parentSuiteId
            )) as any;
        }
    } else {
        for (let i = 0; i < action.payload.requirementIds.length; i++) {
            const param = {
                suiteType: action.payload.suiteType,
                name: action.payload.requirementIds[i].toString(),
                requirementId: action.payload.requirementIds[i],
                parentSuite: { id: action.payload.parentSuiteId, name: action.payload.parentSuiteTitle }
            } as TestSuiteCreateParams;
            const testSuite = (yield effects.call([dataService, dataService.createTestSuite], param, action.payload.planId)) as any;
            testSuites.push(testSuite);
        }
    }
    for (let i = 0; i < testSuites.length; i++) {
        const testSuite = testSuites[i];
        let parentSuite;
        if (testSuiteMap.has(action.payload.parentSuiteId)) {
            parentSuite = testSuiteMap.get(action.payload.parentSuiteId);
        }
        let isSameSuitePresent = false;
        if (parentSuite && parentSuite.hasChildren) {
            // RBS is already existing in the parent suite.
            let childSuiteIndex = parentSuite.children.findIndex((childSuite) => childSuite.id === testSuite.id);
            if (childSuiteIndex >= 0) {
                isSameSuitePresent = true;
            }
        }
        // dispatch load new suite action only if the same suite is already not present in the tree
        if (!isSameSuitePresent) {
            testSuiteList.push(testSuite);
        }
    }
    if (testSuiteList.length > 0) {
        // do not enable editable text field
        const editableSuiteId = -1;
        yield effects.put(SuitesActions.loadNewSuites(testSuiteList, action.payload.parentSuiteId, action.payload.rootSuiteId, editableSuiteId));

        const allTestSuites: TestSuite[] = (yield effects.select(getTestSuites)) as any;
        const treeItemExpandableStateMap: Map<number, boolean> = (yield effects.select(getIdToTreeItemExpandableStateMap)) as any;
        const indexInfo = getSelectionSuiteIndexFromSuiteId(testSuiteList[0].id, allTestSuites, -1, treeItemExpandableStateMap);

        // navigate to the first suite in list of newly created suites
        if (indexInfo.found) {
            action.payload.selection.select(indexInfo.index);
            yield effects.put(SuitesActions.changeSelectedSuite(action.payload.planId, testSuiteList[0].id, testSuiteList[0].name));
        }

        const telemetryPayload = {
            planId: action.payload.planId,
            parentSuiteId: action.payload.parentSuiteId,
            requirementIds: action.payload.requirementIds,
            suiteType: action.payload.suiteType,
            createdSuiteIds: testSuiteList.map((testSuite) => testSuite.id),
            isAdvancedLicenseEnabled: yield effects.select(isAdvancedLicenseEnabled)
        };
        yield effects.put(TelemetryActions.publishTelemetry(TelemetryConstants.NEW_SUITE, telemetryPayload));
    }
    yield effects.put(TestHubRefreshActions.updateBusyViewStatus(false));
}

export function* openDeleteTestSuiteDialog(action: ActionsOfType<SuitesActions, SuitesActionsTypes.openDeleteTestSuiteDialog>): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const layoutManager: IVssLayoutManager = (yield effects.call([pageContext, pageContext.getService], "IVssLayoutManager")) as any;

    const testSuiteMap: Map<number, TestSuite> = (yield effects.select(getIdToTestSuiteMap)) as any;
    const currentSuite = testSuiteMap.get(action.payload.suiteId);

    let childSuitesCount: number = 0;

    if (currentSuite) {
        childSuitesCount = getChildSuiteCount(currentSuite, false);
    }

    yield effects.call([layoutManager, layoutManager.renderCallout], (onDismiss: any) => {
        return React.createElement(
            WrappedComponent,
            {
                wrappedType: "DeleteTestSuiteConfirmationDialog",
                dependencies: ["ms.vss-test-web.delete-test-suite-confirmation-dialog"],
                wrappedProps: {
                    planId: action.payload.planId,
                    suiteId: action.payload.suiteId,
                    rootSuiteId: action.payload.rootSuiteId,
                    suiteName: action.payload.suiteName,
                    suiteType: action.payload.suiteType,
                    selection: action.payload.selection,
                    dispatch: action.payload.dispatch,
                    childSuitesCount: childSuitesCount,
                    onDismiss: onDismiss
                }
            },
            null
        );
    });
}

export function* deleteTestSuite(action: ActionsOfType<SuitesActions, SuitesActionsTypes.deleteTestSuite>): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const dataService: ITestHubDataService = (yield effects.call([pageContext, pageContext.getService], "ITestHubDataService")) as any;

    yield effects.put(TestHubRefreshActions.updateBusyViewStatus(true));
    const treeItemExpandableStateMap: Map<number, boolean> = (yield effects.select(getIdToTreeItemExpandableStateMap)) as any;
    const testSuiteMap: Map<number, TestSuite> = (yield effects.select(getIdToTestSuiteMap)) as any;
    const allTestSuites: TestSuite[] = (yield effects.select(getTestSuites)) as any;
    const currentlySelectedSuiteId: number = (yield effects.select(getSelectedSuiteId)) as any;
    const testSuiteToDelete = testSuiteMap.get(action.payload.suiteId);
    let parentSuiteId = -1,
        parentSuiteName = "";
    if (testSuiteToDelete && testSuiteToDelete.parentSuite) {
        parentSuiteId = testSuiteToDelete.parentSuite.id;
        parentSuiteName = testSuiteToDelete.parentSuite.name;
    }

    yield effects.call([dataService, dataService.deleteTestSuite], action.payload.planId, action.payload.suiteId);
    yield effects.put(SuitesActions.removeSuite(action.payload.suiteId, action.payload.rootSuiteId));

    yield effects.put(TestHubRefreshActions.updateBusyViewStatus(false));
    const telemetryPayload = {
        planId: action.payload.planId,
        suiteId: action.payload.suiteId,
        suiteType: action.payload.suiteType,
        suiteName: action.payload.suiteName,
        isAdvancedLicenseEnabled: yield effects.select(isAdvancedLicenseEnabled)
    };

    if (!testSuiteMap.get(currentlySelectedSuiteId)) {
        // if currently selected Suite Id is not present after deletion we need to select the parent suite and load the test case for it
        if (parentSuiteId && parentSuiteName) {
            const indexInfo = getSelectionSuiteIndexFromSuiteId(parentSuiteId, allTestSuites, -1, treeItemExpandableStateMap);
            const shouldchangeSuite = action.payload.selection.value[0] ? indexInfo.index !== action.payload.selection.value[0].beginIndex : true;
            if (indexInfo.found && shouldchangeSuite) {
                action.payload.selection.select(indexInfo.index);
                yield effects.put(SuitesActions.changeSelectedSuite(action.payload.planId, parentSuiteId, parentSuiteName));
            }
        }
    } else {
        // else change the selection only if the index of the selection has changed due to delete of test suites.
        const indexInfo = getSelectionSuiteIndexFromSuiteId(currentlySelectedSuiteId, allTestSuites, -1, treeItemExpandableStateMap);
        const hasSelectionChanged = action.payload.selection.value[0] ? indexInfo.index !== action.payload.selection.value[0].beginIndex : true;
        if (indexInfo.found && hasSelectionChanged) {
            // reload of suite is not required here
            action.payload.selection.select(indexInfo.index);
        }
    }

    yield effects.put(TelemetryActions.publishTelemetry(TelemetryConstants.DELETE_SUITE, telemetryPayload));
}

export function* createStaticSuite(action: ActionsOfType<SuitesActions, SuitesActionsTypes.createStaticSuite>): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const dataService: ITestHubDataService = (yield effects.call([pageContext, pageContext.getService], "ITestHubDataService")) as any;
    yield effects.put(TestHubRefreshActions.updateBusyViewStatus(true));
    const params = {
        suiteType: action.payload.suiteType,
        name: Resources.NewStaticSuiteDefaultName,
        parentSuite: { id: action.payload.parentSuiteId, name: action.payload.parentSuiteTitle }
    } as TestSuiteCreateParams;
    const testSuite = (yield effects.call([dataService, dataService.createTestSuite], params, action.payload.planId)) as any;
    yield effects.put(SuitesActions.loadNewSuites([testSuite], action.payload.parentSuiteId, action.payload.rootSuiteId, testSuite.id));

    yield effects.put(TestHubRefreshActions.updateBusyViewStatus(false));
    const telemetryPayload = {
        planId: action.payload.planId,
        suiteType: action.payload.suiteType,
        suiteId: testSuite.id,
        isAdvancedLicenseEnabled: yield effects.select(isAdvancedLicenseEnabled)
    };
    yield effects.put(TelemetryActions.publishTelemetry(TelemetryConstants.NEW_SUITE, telemetryPayload));
}

export function* saveTestSuiteName(action: ActionsOfType<SuitesActions, SuitesActionsTypes.saveTestSuiteName>): SagaIterator {
    yield effects.put(SuitesActions.updateTestSuiteName(action.payload.suiteId, action.payload.newSuiteTitle, action.payload.rootSuiteId));
    if (action.payload.oldSuiteTitle != action.payload.newSuiteTitle) {
        const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
        const dataService: ITestHubDataService = (yield effects.call([pageContext, pageContext.getService], "ITestHubDataService")) as any;
        const params = {
            name: action.payload.newSuiteTitle
        } as TestSuiteUpdateParams;
        yield effects.call([dataService, dataService.updateTestSuite], params, action.payload.planId, action.payload.suiteId);

        const telemetryPayload = {
            planId: action.payload.planId,
            suiteType: action.payload.suiteId,
            oldSuiteTitle: action.payload.oldSuiteTitle,
            newSuiteTitle: action.payload.newSuiteTitle,
            isAdvancedLicenseEnabled: yield effects.select(isAdvancedLicenseEnabled)
        };
        yield effects.put(TelemetryActions.publishTelemetry(TelemetryConstants.RENAME_TEST_SUITE, telemetryPayload));
    }
}

export function* openCreateEditSuiteQueryForm(action: ActionsOfType<SuitesActions, SuitesActionsTypes.openCreateEditSuiteQueryForm>): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const layoutManager: IVssLayoutManager = (yield effects.call([pageContext, pageContext.getService], "IVssLayoutManager")) as any;

    const createEditSuiteQueryChannel: Channel<ICreateEditSuiteQueryPayload> = (yield effects.call(channel)) as any;

    const openCreateEditSuiteQueryFormProps = {
        areaPath: action.payload.areaPath,
        createSave: action.payload.createSave,
        queryText: action.payload.queryText,
        onSuccess: (queryName: string, queryText: string) => {
            action.payload.createSave
                ? onCreateSave(
                      pageContext,
                      createEditSuiteQueryChannel,
                      TestSuiteType.DynamicTestSuite,
                      queryName,
                      queryText,
                      action.payload.parentSuiteId,
                      action.payload.parentSuiteTitle,
                      action.payload.planId,
                      action.payload.rootSuiteId
                  )
                : onEdit(pageContext, createEditSuiteQueryChannel, queryText, action.payload.planId, action.payload.parentSuiteId);
        }
    };

    yield effects.call([layoutManager, layoutManager.renderCallout], (onDismiss: any) => {
        const properties: ILegacyComponentProperties = {
            modules: ["TestManagement/Scripts/Shared/CreateNewQBSProxy"],
            wrappedType: "create-qbs-dialog-proxy",
            wrappedProps: openCreateEditSuiteQueryFormProps,
            loadingComponent: () => {
                return React.createElement(Spinner, { className: "spinner-pos", size: SpinnerSize.medium });
            }
        };
        return React.createElement(LegacyComponent, properties);
    });
    const payload: ICreateEditSuiteQueryPayload = (yield effects.take(createEditSuiteQueryChannel)) as any;

    if (action.payload.createSave && action.payload.selection) {
        yield effects.put(
            SuitesActions.createNewQuerySuite(
                payload.testSuite,
                payload.planId,
                payload.parentSuiteId,
                payload.parentSuiteTitle,
                payload.suiteType,
                payload.rootSuiteId,
                payload.queryName,
                payload.queryText,
                action.payload.selection
            )
        );
    } else {
        yield effects.put(
            SuitesActions.editQueryForQueryBasedSuite(payload.queryText, payload.planId, payload.parentSuiteId, payload.parentSuiteTitle)
        );
    }
}

export function* createNewQuerySuite(action: ActionsOfType<SuitesActions, SuitesActionsTypes.createNewQuerySuite>): SagaIterator {
    const editableSuiteId = -1;
    const testSuite = action.payload.testSuite;

    yield effects.put(SuitesActions.loadNewSuites([testSuite], action.payload.parentSuiteId, action.payload.rootSuiteId, editableSuiteId));

    const allTestSuites: TestSuite[] = (yield effects.select(getTestSuites)) as any;
    const treeItemExpandableStateMap: Map<number, boolean> = (yield effects.select(getIdToTreeItemExpandableStateMap)) as any;
    const indexInfo = getSelectionSuiteIndexFromSuiteId(testSuite.id, allTestSuites, -1, treeItemExpandableStateMap);
    // navigate to the newly created suite
    if (indexInfo.found) {
        action.payload.selection.select(indexInfo.index);
        yield effects.put(SuitesActions.changeSelectedSuite(action.payload.planId, testSuite.id, testSuite.name));
    }

    const telemetryPayload = {
        planId: action.payload.planId,
        parentSuiteId: action.payload.parentSuiteId,
        suiteType: action.payload.suiteType,
        createdSuiteId: action.payload.testSuite.id,
        queryName: action.payload.queryName,
        queryText: action.payload.queryText,
        isAdvancedLicenseEnabled: yield effects.select(isAdvancedLicenseEnabled)
    };
    yield effects.put(TelemetryActions.publishTelemetry(TelemetryConstants.NEW_SUITE, telemetryPayload));
}

export function* editQueryForQueryBasedSuite(action: ActionsOfType<SuitesActions, SuitesActionsTypes.editQueryForQueryBasedSuite>): SagaIterator {
    yield effects.put(SuitesActions.changeSelectedSuite(action.payload.planId, action.payload.suiteId, action.payload.suiteTitle));
    yield effects.put(SuitesActions.queryEdited(action.payload.queryText, action.payload.suiteId));
    const telemetryPayload = {
        planId: action.payload.planId,
        suiteType: TestSuiteType.DynamicTestSuite,
        editedSuiteId: action.payload.suiteId,
        queryText: action.payload.queryText,
        isAdvancedLicenseEnabled: yield effects.select(isAdvancedLicenseEnabled)
    };
    yield effects.put(TelemetryActions.publishTelemetry(TelemetryConstants.EDIT_SUITE_QUERY, telemetryPayload));
}

export function* updateTestSuiteParent(action: ActionsOfType<SuitesActions, SuitesActionsTypes.updateTestSuiteParent>): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const dataService: ITestHubDataService = (yield effects.call([pageContext, pageContext.getService], "ITestHubDataService")) as any;
    yield effects.put(TestHubRefreshActions.updateBusyViewStatus(true));
    const params = {
        parentSuite: {
            id: action.payload.targetParentSuiteId
        }
    } as TestSuiteUpdateParams;
    try {
        const testSuite = (yield effects.call(
            [dataService, dataService.updateTestSuite],
            params,
            action.payload.planId,
            action.payload.suiteId
        )) as any;
        yield effects.put(SuitesActions.updateTestSuiteParentSucceeded(action.payload.suiteId, action.payload.targetParentSuiteId));
    } catch (e) {
        yield effects.put(SuitesActions.showToastNotifications(e.message, TestHubRefreshConstants.TOAST_MESSAGE_DURATION));
    }
    yield effects.put(TestHubRefreshActions.updateBusyViewStatus(false));

    const telemetryPayload = {
        planId: action.payload.planId,
        suiteId: action.payload.suiteId,
        isAdvancedLicenseEnabled: yield effects.select(isAdvancedLicenseEnabled)
    };
    yield effects.put(TelemetryActions.publishTelemetry(TelemetryConstants.REORDER_TEST_SUITE, telemetryPayload));
}

export function* reorderTestSuite(action: ActionsOfType<SuitesActions, SuitesActionsTypes.reorderTestSuite>): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const dataService: ITestHubDataService = (yield effects.call([pageContext, pageContext.getService], "ITestHubDataService")) as any;
    const parentSuite = (yield effects.select(getTestSuiteById, action.payload.targetParentSuiteId)) as any;
    yield effects.put(TestHubRefreshActions.updateBusyViewStatus(true));
    let params: SuiteEntryUpdateParams[] = [];
    //shift factor denotes if the items are shifted up or down.
    let shiftFactor: number = 0;
    //we are specifying order of every child of the suite tree when reordering is done
    for (let i = 0; i < parentSuite.children.length; i++) {
        if (parentSuite.children[i].id === action.payload.targetSuiteId) {
            if (action.payload.dropAtEnd) {
                params.push({
                    id: action.payload.targetSuiteId,
                    sequenceNumber: i + shiftFactor,
                    suiteEntryType: SuiteEntryTypes.Suite
                });
                params.push({
                    id: action.payload.suiteId,
                    sequenceNumber: i + shiftFactor + 1,
                    suiteEntryType: SuiteEntryTypes.Suite
                });
            } else {
                params.push({
                    id: action.payload.suiteId,
                    sequenceNumber: i + shiftFactor,
                    suiteEntryType: SuiteEntryTypes.Suite
                });
                params.push({
                    id: action.payload.targetSuiteId,
                    sequenceNumber: i + shiftFactor + 1,
                    suiteEntryType: SuiteEntryTypes.Suite
                });
            }

            if (shiftFactor === 0) {
                shiftFactor = 1;
            } else {
                shiftFactor = 0;
            }
        } else if (parentSuite.children[i].id === action.payload.suiteId) {
            if (shiftFactor === 0) {
                shiftFactor = -1;
            } else {
                shiftFactor = 0;
            }
        } else {
            params.push({
                id: parentSuite.children[i].id,
                sequenceNumber: i + shiftFactor,
                suiteEntryType: SuiteEntryTypes.Suite
            });
        }
    }

    yield effects.call([dataService, dataService.reorderSuiteEntries], params, action.payload.targetParentSuiteId);

    yield effects.put(SuitesActions.reorderTestSuiteSucceeded(action.payload.suiteId, action.payload.targetSuiteId, action.payload.dropAtEnd));
    yield effects.put(TestHubRefreshActions.updateBusyViewStatus(false));

    const telemetryPayload = {
        planId: action.payload.planId,
        suiteId: action.payload.suiteId,
        isAdvancedLicenseEnabled: yield effects.select(isAdvancedLicenseEnabled)
    };
    yield effects.put(TelemetryActions.publishTelemetry(TelemetryConstants.REORDER_TEST_SUITE, telemetryPayload));
}

export function* updateParentAndReorderSuite(action: ActionsOfType<SuitesActions, SuitesActionsTypes.updateParentAndReorderSuite>): SagaIterator {
    //using effects.call to call updateTestSuiteParent and reorderTestSuite functions in Saga. Not using effects.put to call both actions since we need it to be executed sequentially
    yield effects.call(updateTestSuiteParent, {
        payload: {
            planId: action.payload.planId,
            suiteId: action.payload.suiteId,
            targetParentSuiteId: action.payload.targetParentSuiteId
        },
        type: SuitesActionsTypes.updateTestSuiteParent
    });

    yield effects.call(reorderTestSuite, {
        payload: {
            planId: action.payload.planId,
            targetParentSuiteId: action.payload.targetParentSuiteId,
            suiteId: action.payload.suiteId,
            targetSuiteId: action.payload.targetSuiteId,
            dropAtEnd: action.payload.dropAtEnd
        },
        type: SuitesActionsTypes.reorderTestSuite
    });
}

export function* moveTestCaseToSuite(action: ActionsOfType<SuitesActions, SuitesActionsTypes.moveTestCaseToSuite>): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const moveTestCaseChannel: Channel<any> = (yield effects.call(channel)) as any;
    yield effects.put(TestHubRefreshActions.updateBusyViewStatus(true));
    const tfsPageService: ITfsPageService = (yield effects.call([pageContext, pageContext.getService], "ITfsPageService")) as any;
    const tfsPageData = (yield effects.call([tfsPageService, tfsPageService.getData])) as any;
    const layoutManager: IVssLayoutManager = (yield effects.call([pageContext, pageContext.getService], "IVssLayoutManager")) as any;
    const moveTestCasesToSuiteProps = {
        tfsContext: tfsPageData,
        planId: action.payload.testPlanId,
        sourceSuiteId: action.payload.sourceSuiteId,
        sourceSuiteRevision: action.payload.sourceSuiteRevision,
        destinationSuiteId: action.payload.destinationSuiteId,
        destinationSuiteRevision: action.payload.destinationSuiteRevision,
        testCaseIds: action.payload.testCaseIds,
        successCallBack: (testSuites: any) => {
            moveTestCaseChannel.put({ testSuites: testSuites });
        },
        errorCallBack: (error: any) => {
            moveTestCaseChannel.put({ error: error });
        }
    };

    yield effects.call([layoutManager, layoutManager.renderCallout], (onDismiss: any) => {
        const properties: ILegacyComponentProperties = {
            modules: ["TestManagement/Scripts/Shared/MoveTestCaseToSuiteProxy"],
            wrappedType: "move-test-case-to-suite-proxy",
            wrappedProps: moveTestCasesToSuiteProps
        };
        return React.createElement(LegacyComponent, properties);
    });
    const payload = (yield effects.take(moveTestCaseChannel)) as any;
    if (payload) {
        if (payload.testSuites) {
            yield effects.put(TestHubRefreshActions.updateBusyViewStatus(false));
            yield effects.put(SuitesActions.moveTestCaseToSuiteSucceeded(action.payload.testCaseIds));
            const telemetryPayload = {
                planId: action.payload.testPlanId,
                sourceSuiteId: action.payload.sourceSuiteId,
                destinationSuiteId: action.payload.destinationSuiteId,
                testCaseIds: action.payload.testCaseIds,
                isAdvancedLicenseEnabled: yield effects.select(isAdvancedLicenseEnabled)
            };
            yield effects.put(TelemetryActions.publishTelemetry(TelemetryConstants.MOVE_TEST_CASES_TO_SUITE, telemetryPayload));
        } else {
            yield effects.put(TestHubRefreshActions.updateBusyViewStatus(false));
            yield effects.put(SuitesActions.showToastNotifications(payload.error.message, TestHubRefreshConstants.TOAST_MESSAGE_DURATION));
        }
    }
}

export function* assignTestersToSuite(action: ActionsOfType<SuitesActions, SuitesActionsTypes.assignTestersToSuite>): SagaIterator {
    const assignTestersChannel: Channel<any> = (yield effects.call(channel)) as any;
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const layoutManager: IVssLayoutManager = (yield effects.call([pageContext, pageContext.getService], "IVssLayoutManager")) as any;
    const assignTestersToSuiteProps = {
        planId: action.payload.planId,
        suite: action.payload.suite,
        dialogClosedCallBack: (changesSaved: boolean) => {
            if (changesSaved) {
                onSave(changesSaved, assignTestersChannel);
            }
        }
    };

    yield effects.call([layoutManager, layoutManager.renderCallout], (onDismiss: any) => {
        const properties: ILegacyComponentProperties = {
            modules: ["TestManagement/Scripts/Shared/AssignTestersToSuiteProxy"],
            wrappedType: "assign-testers-dialog-proxy",
            wrappedProps: assignTestersToSuiteProps,
            loadingComponent: () => {
                return React.createElement(Spinner, { className: "spinner-pos", size: SpinnerSize.medium });
            }
        };
        return React.createElement(LegacyComponent, properties);
    });

    const payload = (yield effects.take(assignTestersChannel)) as any;
    if (payload) {
        yield effects.put(SuitesActions.loadTestPointsForSelectedSuite(action.payload.planId, action.payload.suite.id));

        const telemetryPayload = {
            planId: action.payload.planId,
            suiteId: action.payload.suite.id,
            suiteType: action.payload.suite.type,
            isAdvancedLicenseEnabled: yield effects.select(isAdvancedLicenseEnabled)
        };
        yield effects.put(TelemetryActions.publishTelemetry(TelemetryConstants.ASSIGN_TESTERS_TO_SUITE, telemetryPayload));
    }
}

export function* openExportHTMLDialog(action: ActionsOfType<SuitesActions, SuitesActionsTypes.openExportHTMLDialog>): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    yield effects.put(TestHubRefreshActions.updateBusyViewStatus(true));
    const dataService: ITestHubDataService = (yield effects.call([pageContext, pageContext.getService], "ITestHubDataService")) as any;
    const testPlan: TestPlan = (yield effects.call([dataService, dataService.getTestPlanById], action.payload.planId)) as any;
    const layoutManager = (yield effects.call([pageContext, pageContext.getService], "IVssLayoutManager")) as any;

    const testSuiteMap: Map<number, TestSuite> = (yield effects.select(getIdToTestSuiteMap)) as any;
    const currentSuite = testSuiteMap.get(action.payload.suiteId);
    const rootSuite = testSuiteMap.get(action.payload.rootSuiteId);
    let defineColumnOptionsFields: string[] = (yield effects.select(getDefineColumnOptionFields)) as any;
    let defineTabColumnOptions = getDefineTabColumns(defineColumnOptionsFields);
    let executeColumnOptionsFields: string[] = (yield effects.select(getExecuteColumnOptionFields)) as any;
    let executeTabColumnOptions = getExecuteTabColumns(executeColumnOptionsFields);

    let executeAndDefineColumnOptions = deduplicateAndConcatenateColumnOptions(defineTabColumnOptions, executeTabColumnOptions);
    let currentColumnOptions = [];

    for (const columnOptionField of executeAndDefineColumnOptions) {
        currentColumnOptions.push({
            fieldId: columnOptionField.id,
            text: columnOptionField.name
        });
    }

    if (currentSuite && rootSuite) {
        const childSuiteIds = getChildSuiteIds(currentSuite);
        let isChildSuitesCountExceededForExportFeature: boolean = checkIfThresholdExceededForExportFeature(currentSuite);
        const exportHTMLDialogProps = {
            currentPlan: {
                area: {
                    name: testPlan.areaPath
                },
                automatedTestEnvironment: testPlan.automatedTestEnvironment,
                automatedTestSettings: testPlan.automatedTestEnvironment,
                build: {
                    id: testPlan.buildId
                },
                buildDefinition: testPlan.buildDefinition,
                description: testPlan.description,
                endDate: testPlan.endDate,
                id: testPlan.id,
                iteration: testPlan.iteration,
                manualTestEnvironment: testPlan.manualTestEnvironment,
                manualTestSettings: testPlan.manualTestSettings,
                name: testPlan.name,
                owner: testPlan.owner,
                previousBuild: {
                    id: testPlan.previousBuildId
                },
                project: testPlan.project,
                releaseEnvironmentDefinition: testPlan.releaseEnvironmentDefinition,
                revision: testPlan.revision,
                rootSuite: testPlan.rootSuite,
                startDate: testPlan.startDate,
                state: testPlan.state,
                testOutcomeSettings: testPlan.testOutcomeSettings,
                updatedBy: testPlan.updatedBy,
                updatedDate: testPlan.updatedDate
            },
            id: action.payload.suiteId,
            revision: currentSuite.revision,
            title: currentSuite.name,
            childSuiteIds: childSuiteIds,
            pointCount: action.payload.pointCount,
            type: currentSuite.suiteType,
            requirementId: currentSuite.requirementId,
            parentSuiteId: currentSuite.parentSuite ? currentSuite.parentSuite.id : 0,
            queryText: currentSuite.queryString,
            status: "",
            totalPointCount: action.payload.totalPointCount,
            configurations: currentSuite.defaultConfigurations,
            isChildSuitesCountExceededForExportFeature: isChildSuitesCountExceededForExportFeature,
            rootSuite: rootSuite,
            savedColumns: currentColumnOptions
        };

        yield effects.call([layoutManager, layoutManager.renderCallout], (onDismiss: any) => {
            const properties: ILegacyComponentProperties = {
                modules: ["TestManagement/Scripts/Shared/ExportHTMLDialogProxy"],
                wrappedType: "export-html-dialog-proxy",
                wrappedProps: exportHTMLDialogProps
            };
            return React.createElement(LegacyComponent, properties);
        });
    }
    yield effects.put(TestHubRefreshActions.updateBusyViewStatus(false));
    const telemetryPayload = {
        planId: action.payload.planId,
        suiteId: action.payload.suiteId,
        isAdvancedLicenseEnabled: yield effects.select(isAdvancedLicenseEnabled)
    };
    yield effects.put(TelemetryActions.publishTelemetry(TelemetryConstants.EXPORT_HTML, telemetryPayload));
}

export function* saveTestCasePreviewStatus(
    action: ActionsOfType<TestHubRefreshActions, TestHubRefreshActionsTypes.saveTestCasePreviewStatus>
): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const settingsService: ISettingsService = (yield effects.call([pageContext, pageContext.getService], "ISettingsService")) as any;

    // save the preview status in db
    if (action.payload && action.payload.previewStatus) {
        let registryEntries;
        registryEntries = {
            [`${TestHubRefreshConstants.PREVIEW_SPLITTER_STATUS_KEY}/${TestHubRefreshConstants.SETTINGS_KEY}`]: action.payload.previewStatus
        };
        yield effects.call([settingsService, settingsService.setEntries], registryEntries, SettingsUserScope.Me);
    }

    const telemetryPayload = {
        previewStatus: action.payload.previewStatus,
        isAdvancedLicenseEnabled: yield effects.select(isAdvancedLicenseEnabled)
    };

    yield effects.put(TelemetryActions.publishTelemetry(TelemetryConstants.TOGGLE_TEST_CASE_PREVIEW, telemetryPayload));
    yield effects.put(TestHubRefreshActions.changeTestCasePreviewStatus(action.payload.previewStatus));
}

export function* saveSplitterWidth(action: ActionsOfType<SuitesActions, SuitesActionsTypes.saveSplitterWidth>): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const settingsService: ISettingsService = (yield effects.call([pageContext, pageContext.getService], "ISettingsService")) as any;
    const tfsPageService: ITfsPageService = (yield effects.call([pageContext, pageContext.getService], "ITfsPageService")) as any;
    const tfsPageData = (yield effects.call([tfsPageService, tfsPageService.getData])) as any;

    // save the splitter width in db
    if (action.payload && action.payload.splitterWidth) {
        let registryEntries;
        registryEntries = {
            splitterWidth: action.payload.splitterWidth
        };
        yield effects.call([settingsService, settingsService.setEntries], registryEntries, SettingsUserScope.Me, "project", tfsPageData.project.id);
    }

    yield effects.put(TestHubRefreshActions.changeSplitterWidth(action.payload.splitterWidth));
}

export function* fetchTestCasePreviewStatus(
    action: ActionsOfType<TestHubRefreshActions, TestHubRefreshActionsTypes.fetchTestCasePreviewStatus>
): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const settingsService: ISettingsService = (yield effects.call([pageContext, pageContext.getService], "ISettingsService")) as any;

    // fetch the preview status from db
    let result: { [key: string]: any } = (yield effects.call(
        [settingsService, settingsService.getEntry],
        TestHubRefreshConstants.PREVIEW_SPLITTER_STATUS_KEY,
        SettingsUserScope.Me
    )) as any;
    if (result && result[TestHubRefreshConstants.SETTINGS_KEY]) {
        yield effects.put(TestHubRefreshActions.changeTestCasePreviewStatus(result[TestHubRefreshConstants.SETTINGS_KEY]));
    }
}

export function* fetchSplitterWidth(action: ActionsOfType<SuitesActions, SuitesActionsTypes.fetchSplitterWidth>): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const settingsService: ISettingsService = (yield effects.call([pageContext, pageContext.getService], "ISettingsService")) as any;
    const pageService = (yield effects.call([pageContext, pageContext.getService], "ITfsPageService")) as any;
    const pageData = (yield effects.call([pageService, pageService.getData])) as any;

    // fetch the splitter width from db
    let result: { [key: string]: any } = (yield effects.call(
        [settingsService, settingsService.getEntriesAsync],
        "",
        SettingsUserScope.Me,
        "project",
        pageData.project.id
    )) as any;
    if (result && result["splitterWidth"]) {
        yield effects.put(TestHubRefreshActions.changeSplitterWidth(result["splitterWidth"]));
    }
}

export function* showToastNotifications(action: ActionsOfType<SuitesActions, SuitesActionsTypes.showToastNotifications>): SagaIterator {
    try {
        const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
        const globalMessageService: IGlobalMessagesService = (yield effects.call(
            [pageContext, pageContext.getService],
            "IGlobalMessagesService"
        )) as any;
        yield effects.call([globalMessageService, globalMessageService.addToast], {
            duration: action.payload.toastDurationMs,
            message: action.payload.toastMessage,
            callToAction: action.payload.callToActionText,
            onCallToActionClick: action.payload.callToActionTextCallback,
            className: action.payload.className
        });
    } catch {}
}

export function* openRequirementWorkItemForm(action: ActionsOfType<SuitesActions, SuitesActionsTypes.openRequirementWorkItemForm>): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const contributionService: IVssContributionService = (yield effects.call(
        [pageContext, pageContext.getService],
        "IVssContributionService"
    )) as any;
    yield effects.call(
        [contributionService, contributionService.getContributionsAsync],
        ["ms.vss-features.ui-responsive-viewport-content", "ms.vss-work-web.common-content"]
    );

    const workItemService: IWorkItemService = (yield effects.call([pageContext, pageContext.getService], "work-item-service")) as any;
    if (workItemService) {
        yield effects.call([workItemService, workItemService.openWorkItem], action.payload.requirementId);
    }
}

export function* getAllPageUrl(action: ActionsOfType<SuitesActions, SuitesActionsTypes.getMineAllPageUrl>): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const tfsPageService: ITfsPageService = (yield effects.call([pageContext, pageContext.getService], "ITfsPageService")) as any;
    const tfsPageData = (yield effects.call([tfsPageService, tfsPageService.getData])) as any;
    const routeValues: { [key: string]: string } = {
        pivot: RouteParametersMineAllPage.Pivot,
        project: tfsPageData.project.name
    };
    const locationService: IVssLocationService = (yield effects.call([pageContext, pageContext.getService], "IVssLocationService")) as any;
    //constructing mine/all page url using contribution id
    let url = (yield effects.call([locationService, locationService.routeUrl], RouteParametersMineAllPage.ContributionId, routeValues)) as any;
    url += RouteParametersMineAllPage.queryParams;
    yield effects.put(TestHubRefreshActions.loadMineAllPageUrl(url));
}

/**
 * This will return the child nodes count of the node, this method will call in recursion
 * to get nodes count, it will break when child nodes count is more than test suites limit
 * for the export html feature
 * @param node
 */

function getChildSuiteIds(testSuite: TestSuite): number[] {
    let children = testSuite.children || [];
    let childrenLength = children.length;

    if (childrenLength === 0) {
        return [];
    }
    let childSuiteIds: number[] = [];

    for (let i = 0; i < childrenLength; i++) {
        childSuiteIds.concat(children[i].id);
    }
    return childSuiteIds;
}

function checkIfThresholdExceededForExportFeature(testSuite: TestSuite): boolean {
    let result: boolean = false;
    let count = getChildSuiteCount(testSuite, true);

    if (count >= SuitesConstants.TestSuitesLimit) {
        result = true;
    }

    return result;
}

function deduplicateAndConcatenateColumnOptions(defineTabColumnOptions: any, executeTabColumnOptions: any): any {
    let defineTabColumnOptionsLength: number = defineTabColumnOptions.length;
    let executeTabColumnOptionsLength: number = executeTabColumnOptions.length;

    let duplicateFlag: boolean;
    for (let i = 0; i < executeTabColumnOptionsLength; i++) {
        duplicateFlag = false;
        for (let j = 0; j < defineTabColumnOptionsLength; j++) {
            if (defineTabColumnOptions[j].id == executeTabColumnOptions[i].id) {
                duplicateFlag = true;
                break;
            }
        }
        if (!duplicateFlag) {
            defineTabColumnOptions.push(executeTabColumnOptions[i]);
        }
    }
    return defineTabColumnOptions;
}

function onSave(changesSaved: boolean, assignTestersChannel: Channel<any>) {
    if (changesSaved) {
        assignTestersChannel.put({ changesSaved: changesSaved });
    }
}

function getSuitesCountAndHierarchy(suites: TestSuite[]): ILoadSuitesTelemetryPayload {
    const telemetryPayload: ILoadSuitesTelemetryPayload = { rbs: 0, qbs: 0, static: 0, hierarchy: [] };
    const suitesLength = suites.length;
    telemetryPayload.hierarchy[0] = suitesLength;
    for (let i = 0; i < suitesLength; i++) {
        let childPayload: ILoadSuitesTelemetryPayload = { rbs: 0, qbs: 0, static: 0, hierarchy: [] };
        if (suites[i].hasChildren) {
            childPayload = getSuitesCountAndHierarchy(suites[i].children);
        }
        switch (suites[i].suiteType) {
            case TestSuiteType.DynamicTestSuite:
                telemetryPayload.qbs += 1;
                break;
            case TestSuiteType.RequirementTestSuite:
                telemetryPayload.rbs += 1;
                break;
            default:
                telemetryPayload.static += 1;
                break;
        }
        telemetryPayload.qbs += childPayload.qbs;
        telemetryPayload.rbs += childPayload.rbs;
        telemetryPayload.static += childPayload.static;
        // finding total number of suites present at each level
        for (let j = 0; j < childPayload.hierarchy.length; j++) {
            if (!telemetryPayload.hierarchy[j + 1]) {
                telemetryPayload.hierarchy[j + 1] = 0;
            }
            telemetryPayload.hierarchy[j + 1] += childPayload.hierarchy[j] ? childPayload.hierarchy[j] : 0;
        }
    }
    return telemetryPayload;
}

function onEdit(
    pageContext: IVssPageContext,
    createSaveChannel: Channel<ICreateEditSuiteQueryPayload>,
    queryString: string,
    planId: number,
    suiteId: number
) {
    const dataService: ITestHubDataService = pageContext.getService<ITestHubDataService>("ITestHubDataService");
    if (dataService) {
        const params = {
            queryString: queryString
        } as TestSuiteUpdateParams;
        dataService.updateTestSuite(params, planId, suiteId).then((testSuite) => {
            createSaveChannel.put({
                testSuite: testSuite,
                planId: planId,
                parentSuiteId: suiteId,
                parentSuiteTitle: "",
                suiteType: TestSuiteType.DynamicTestSuite,
                rootSuiteId: 0,
                queryName: "",
                queryText: queryString
            });
        });
    } else {
        throw new Error("Unable to fetch hub data service instance.");
    }
}

function onCreateSave(
    pageContext: IVssPageContext,
    createSaveChannel: Channel<ICreateEditSuiteQueryPayload>,
    suiteType: TestSuiteType,
    suiteName: string,
    queryString: string,
    parentSuiteId: number,
    parentSuiteTitle: string,
    planId: number,
    rootSuiteId: number
) {
    const dataService: ITestHubDataService = pageContext.getService<ITestHubDataService>("ITestHubDataService");
    if (dataService) {
        const params = {
            suiteType: suiteType,
            name: suiteName,
            queryString: queryString,
            parentSuite: { id: parentSuiteId, name: parentSuiteTitle }
        } as TestSuiteCreateParams;

        dataService.createTestSuite(params, planId).then((testSuite) => {
            createSaveChannel.put({
                testSuite: testSuite,
                planId: planId,
                parentSuiteId: parentSuiteId,
                parentSuiteTitle: parentSuiteTitle,
                suiteType: suiteType,
                rootSuiteId: rootSuiteId,
                queryName: suiteName,
                queryText: queryString
            });
        });
    } else {
        throw new Error("Unable to fetch hub data service instance.");
    }
}

function getChildSuiteCount(testSuite: TestSuite, stopOnExceedingTestSuitesLimit: boolean): number {
    //The second argument acts as a flag to stop on exceeding threshold for exportHTMLDialog
    if (!testSuite) {
        return 0;
    }

    let nextLevelChildren: TestSuite[] = testSuite.children || [];
    let childSuitesCount: number = nextLevelChildren.length;

    for (let i = 0; i < nextLevelChildren.length; i++) {
        childSuitesCount += getChildSuiteCount(nextLevelChildren[i], stopOnExceedingTestSuitesLimit);

        if (stopOnExceedingTestSuitesLimit && childSuitesCount >= SuitesConstants.TestSuitesLimit) {
            return childSuitesCount;
        }
    }

    return childSuitesCount;
}

function filterItemHasValue(filterItemState: IFilterItemState | null): boolean {
    return filterItemState && filterItemState!.value && filterItemState!.value.length > 0;
}

interface ILoadSuitesTelemetryPayload {
    rbs: number;
    qbs: number;
    static: number;
    hierarchy: number[];
}

interface IUpdateTestSuitePayload {
    suiteId: number;
    suiteTitle: string;
    rootSuiteId: number;
}

const _reloadTestPlanAndSuites = errorHandledSaga(function* (
    action: ActionsOfType<SuitesActions, SuitesActionsTypes.reloadTestPlanAndSuites>
): SagaIterator {
    yield effects.call(reloadTestPlanAndSuites, action);
});

const _saveTestCasePreviewStatus = errorHandledSaga(function* (
    action: ActionsOfType<TestHubRefreshActions, TestHubRefreshActionsTypes.saveTestCasePreviewStatus>
): SagaIterator {
    yield effects.call(saveTestCasePreviewStatus, action);
});

const _fetchTestCasePreviewStatus = errorHandledSaga(function* (
    action: ActionsOfType<TestHubRefreshActions, TestHubRefreshActionsTypes.fetchTestCasePreviewStatus>
): SagaIterator {
    yield effects.call(fetchTestCasePreviewStatus, action);
});

const _getSuiteCloneOpState = errorHandledSaga(function* (
    action: ActionsOfType<SuitesActions, SuitesActionsTypes.getSuiteCloneOpState>
): SagaIterator {
    yield effects.call(getSuiteCloneOpState, action);
});

const _loadTestData = errorHandledSaga(function* (action: ActionsOfType<SuitesActions, SuitesActionsTypes.loadInitialTestData>): SagaIterator {
    yield effects.call(loadTestData, action);
});

const _getMineAllPageUrl = errorHandledSaga(function* (action: ActionsOfType<SuitesActions, SuitesActionsTypes.getMineAllPageUrl>): SagaIterator {
    yield effects.call(getAllPageUrl, action);
});

const _getSuitePointCount = errorHandledSaga(function* (action: ActionsOfType<SuitesActions, SuitesActionsTypes.getSuitePointCount>): SagaIterator {
    yield effects.call(getSuitePointCount, action);
});

const _getSuiteTestCasesCount = errorHandledSaga(function* (
    action: ActionsOfType<SuitesActions, SuitesActionsTypes.getSuiteTestCasesCount>
): SagaIterator {
    yield effects.call(getSuiteTestCasesCount, action);
});

const _openTestSuiteWitForm = errorHandledSaga(function* (
    action: ActionsOfType<SuitesActions, SuitesActionsTypes.openTestSuiteWitForm>
): SagaIterator {
    yield effects.call(openTestSuiteWitForm, action);
});

const _createNewReqSuite = errorHandledSaga(function* (action: ActionsOfType<SuitesActions, SuitesActionsTypes.createNewReqSuite>): SagaIterator {
    yield effects.call(createNewReqSuite, action);
});

const _openDeleteTestSuiteDialog = errorHandledSaga(function* (
    action: ActionsOfType<SuitesActions, SuitesActionsTypes.openDeleteTestSuiteDialog>
): SagaIterator {
    yield effects.call(openDeleteTestSuiteDialog, action);
});

const _deleteTestSuiteAction = errorHandledSaga(function* (action: ActionsOfType<SuitesActions, SuitesActionsTypes.deleteTestSuite>): SagaIterator {
    yield effects.call(deleteTestSuite, action);
});

const _createStaticSuite = errorHandledSaga(function* (action: ActionsOfType<SuitesActions, SuitesActionsTypes.createStaticSuite>): SagaIterator {
    yield effects.call(createStaticSuite, action);
});

const _saveTestSuiteName = errorHandledSaga(function* (action: ActionsOfType<SuitesActions, SuitesActionsTypes.saveTestSuiteName>): SagaIterator {
    yield effects.call(saveTestSuiteName, action);
});

const _openCreateEditSuiteQueryForm = errorHandledSaga(function* (
    action: ActionsOfType<SuitesActions, SuitesActionsTypes.openCreateEditSuiteQueryForm>
): SagaIterator {
    yield effects.call(openCreateEditSuiteQueryForm, action);
});

const _createNewQuerySuite = errorHandledSaga(function* (action: ActionsOfType<SuitesActions, SuitesActionsTypes.createNewQuerySuite>): SagaIterator {
    yield effects.call(createNewQuerySuite, action);
});

const _editQueryForQueryBasedSuite = errorHandledSaga(function* (
    action: ActionsOfType<SuitesActions, SuitesActionsTypes.editQueryForQueryBasedSuite>
): SagaIterator {
    yield effects.call(editQueryForQueryBasedSuite, action);
});

const _updateTestSuiteParent = errorHandledSaga(function* (
    action: ActionsOfType<SuitesActions, SuitesActionsTypes.updateTestSuiteParent>
): SagaIterator {
    yield effects.call(updateTestSuiteParent, action);
});

const _reorderTestSuite = errorHandledSaga(function* (action: ActionsOfType<SuitesActions, SuitesActionsTypes.reorderTestSuite>): SagaIterator {
    yield effects.call(reorderTestSuite, action);
});

const _updateParentAndReorderSuite = errorHandledSaga(function* (
    action: ActionsOfType<SuitesActions, SuitesActionsTypes.updateParentAndReorderSuite>
): SagaIterator {
    yield effects.call(updateParentAndReorderSuite, action);
});

const _moveTestCaseToSuite = errorHandledSaga(function* (action: ActionsOfType<SuitesActions, SuitesActionsTypes.moveTestCaseToSuite>): SagaIterator {
    yield effects.call(moveTestCaseToSuite, action);
});

const _assignTestersToSuite = errorHandledSaga(function* (
    action: ActionsOfType<SuitesActions, SuitesActionsTypes.assignTestersToSuite>
): SagaIterator {
    yield effects.call(assignTestersToSuite, action);
});

const _openExportHTMLDialog = errorHandledSaga(function* (
    action: ActionsOfType<SuitesActions, SuitesActionsTypes.openExportHTMLDialog>
): SagaIterator {
    yield effects.call(openExportHTMLDialog, action);
});

const _saveSplitterWidth = errorHandledSaga(function* (action: ActionsOfType<SuitesActions, SuitesActionsTypes.saveSplitterWidth>): SagaIterator {
    yield effects.call(saveSplitterWidth, action);
});

const _fetchSplitterWidth = errorHandledSaga(function* (action: ActionsOfType<SuitesActions, SuitesActionsTypes.fetchSplitterWidth>): SagaIterator {
    yield effects.call(fetchSplitterWidth, action);
});

const _openRequirementWorkItemForm = errorHandledSaga(function* (
    action: ActionsOfType<SuitesActions, SuitesActionsTypes.openRequirementWorkItemForm>
): SagaIterator {
    yield effects.call(openRequirementWorkItemForm, action);
});
